import React, { useRef } from 'react';
import Tooltip from '../Tooltip';
import type { TextColor } from './Text';
import type { FontSize } from './textStyles';
import { getFontSize, getTextColor } from './textStyles';

export interface EllipsisTextProps {
    children: React.ReactNode;
    tooltip?: React.ReactNode | string;
    /**
     * Defaults to filling the container. Pass a fixed width only when the text should
     * truncate before its container runs out of room (e.g. a column with a hard cap).
     */
    maxWidth?: string;
    color?: TextColor;
    size?: FontSize;
    /**
     * Show `tooltip` only when the text is actually cut off. Opt-in, because `tooltip` is any
     * node: a caller repeating the label wants this, and one supplying something the label does
     * not say — a full path, a timestamp — would lose it.
     */
    tooltipWhenTruncated?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

const EllipsisText: React.FC<EllipsisTextProps> = ({
    children,
    tooltip,
    maxWidth = '100%',
    color = 'primary',
    size,
    tooltipWhenTruncated = false,
    className = '',
    style = {},
}) => {
    const ellipsisStyles: React.CSSProperties = {
        color: getTextColor(color),
        ...(size ? { fontSize: getFontSize(size) } : {}),
        maxWidth,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        verticalAlign: 'top',
        ...style,
    };

    const textRef = useRef<HTMLSpanElement>(null);

    const textElement = (
        <span ref={textRef} className={className} style={ellipsisStyles}>
            {children}
        </span>
    );

    if (tooltip) {
        // Measured on hover, not at mount: what fits depends on the container and on webfonts
        // that land after `DOMContentLoaded`, so a value read once is wrong for the rest of the
        // page's life.
        // Without `isShrinkable` the tooltip wrapper is floored at min-content and a percentage `maxWidth` never truncates.
        return (
            <Tooltip
                text={tooltip}
                isShrinkable
                shouldShow={
                    tooltipWhenTruncated
                        ? () => {
                              const node = textRef.current;
                              return !!node && node.scrollWidth > node.clientWidth;
                          }
                        : undefined
                }
            >
                {textElement}
            </Tooltip>
        );
    }

    return textElement;
};

export default EllipsisText;
